import { ChartCard } from '@backrs_ui/common-ui/src/atoms/Charts/ChartCard';

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

type TableRowData = Record<string, string | number>;

type DataTableProps = Readonly<{
  title: string;
  tooltipText?: string;
  data: {
    headers: string[];
    rows: TableRowData[];
  };
}>;

export const DataTable = ({ title, tooltipText, data }: DataTableProps) => {
  const table = (
    <TableContainer sx={containerSx}>
      <Table aria-label="data table" size="small" sx={tableSx}>
        <TableHead>
          <TableRow>
            {data.headers.map((header) => (
              <TableCell key={header} sx={headerCellSx}>
                <Typography
                  variant="text-xs-semibold"
                  color={({ backrs }) => backrs.colors['Blue/600']}
                >
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.rows.map((row, rowIndex) => (
            <TableRow key={`row-${rowIndex}`} sx={rowSx}>
              {data.headers.map((header, headerIndex) => (
                <TableCell key={`${rowIndex}-${headerIndex}`} sx={cellSx}>
                  <Typography
                    variant={headerIndex === 0 ? 'text-sm-medium' : 'text-sm-regular'}
                    color={({ backrs }) =>
                      headerIndex === 0
                        ? backrs.colors['Colors/Text/text-primary']
                        : backrs.colors['Colors/Text/text-tertiary']
                    }
                  >
                    {row[header]}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <ChartCard
      title={title}
      overrideSx={{ container: chartCardContainerSx, content: chartCardContentSx }}
      additionalInfoVariant={
        tooltipText
          ? {
              type: 'TOOLTIP',
              toolTipText: tooltipText,
            }
          : undefined
      }
      chartVariant={{
        type: 'CUSTOM',
        children: table,
      }}
    />
  );
};

const containerSx: SxProps<Theme> = {
  width: {
    xs: 'auto',
    sm: 'max-content',
  },
};

const tableSx: SxProps<Theme> = {
  width: 'auto',
};

const headerCellSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  paddingY: backrs.spacing['spacing-lg'],
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-3xl'],
  },
});

const rowSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  '&:last-child': { borderBottom: 0 },
});

const cellSx: SxProps<Theme> = ({ backrs }) => ({
  paddingY: backrs.spacing['spacing-xl'],
  paddingX: {
    xs: backrs.spacing['spacing-2xl'],
    sm: backrs.spacing['spacing-3xl'],
  },
});

const chartCardContainerSx: SxProps<Theme> = {
  maxWidth: 'fit-content',
};

const chartCardContentSx: SxProps<Theme> = {
  padding: 0,
  overflow: 'hidden',
};
